import { Controller } from "@hotwired/stimulus"

// QAN-383: Table / Card view toggle for the Business Loan Services dashboard.
//
// Both views are rendered server-side; this controller only shows/hides them,
// remembers the choice in localStorage, and powers the per-column client-side
// filter + sort in the Card View. It also handles the inline Status save and
// the Actions "Mark as …" links on each card without a full page reload,
// PATCHing the existing /admin/service_records/:id endpoints as JSON.
export default class extends Controller {
  static targets = ["tableView", "cardView", "tableButton", "cardButton"]

  static STORAGE_KEY = "serviceRecordsView"

  connect() {
    const saved = window.localStorage.getItem(this.constructor.STORAGE_KEY)
    if (saved === "card") {
      this.showCard()
    } else {
      this.showTable()
    }
  }

  // ── View toggle ──

  showTable() {
    this.tableViewTarget.classList.remove("hidden")
    this.cardViewTarget.classList.add("hidden")
    this.setActive(this.tableButtonTarget, this.cardButtonTarget)
    window.localStorage.setItem(this.constructor.STORAGE_KEY, "table")
  }

  showCard() {
    this.cardViewTarget.classList.remove("hidden")
    this.tableViewTarget.classList.add("hidden")
    this.setActive(this.cardButtonTarget, this.tableButtonTarget)
    window.localStorage.setItem(this.constructor.STORAGE_KEY, "card")
  }

  // QAN-531: as in compliance_dashboard_controller — move the design system's
  // `active` class rather than swapping Tailwind colour utilities. The buttons
  // come from admin/shared/_view_toggle and admin_design.scss owns their states.
  setActive(active, inactive) {
    if (!active || !inactive) return
    active.classList.add("active")
    inactive.classList.remove("active")
  }

  // ── Per-column filter ──

  filterColumn(event) {
    const input = event.target
    const term = input.value.trim().toLowerCase()
    const column = input.closest("[data-column]")
    if (!column) return

    column.querySelectorAll("[data-card]").forEach((card) => {
      const haystack = `${card.dataset.client || ""} ${card.dataset.note || ""}`.toLowerCase()
      card.classList.toggle("hidden", term !== "" && !haystack.includes(term))
    })
  }

  // ── Per-column sort ──

  sortColumn(event) {
    const select = event.target
    const key = select.value
    const column = select.closest("[data-column]")
    if (!column) return

    const list = column.querySelector("[data-card-list]")
    if (!list) return

    const cards = Array.from(list.querySelectorAll("[data-card]"))
    cards.sort((a, b) => this.compare(a, b, key))
    cards.forEach((card) => list.appendChild(card))
  }

  compare(a, b, key) {
    switch (key) {
      case "status":
        return (a.dataset.status || "").localeCompare(b.dataset.status || "")
      case "start":
        return (a.dataset.start || "").localeCompare(b.dataset.start || "")
      case "created":
        return (a.dataset.created || "").localeCompare(b.dataset.created || "")
      case "client":
      default:
        return (a.dataset.client || "").localeCompare(b.dataset.client || "")
    }
  }

  // ── Inline status save ──

  async saveStatus(event) {
    event.preventDefault()
    const button = event.currentTarget
    const card = button.closest("[data-card]")
    if (!card) return

    const select = card.querySelector("[data-status-select]")
    const url = button.dataset.url
    if (!select || !url) return

    const feedbackEl = card.querySelector("[data-status-feedback]")
    this.setFeedback(feedbackEl, "Saving…", "text-gray-400")

    const result = await this.patchStatus(url, select.value)
    if (result && result.status) {
      select.dataset.original = select.value
      this.applyStatus(card, result.status)
      this.setFeedback(feedbackEl, "Saved", "text-green-600")
    } else {
      select.value = select.dataset.original || select.value
      this.setFeedback(feedbackEl, "Error", "text-red-600")
    }
  }

  // ── Actions "Mark as …" (no page reload) ──

  async markStatus(event) {
    event.preventDefault()
    const link = event.currentTarget
    const card = link.closest("[data-card]")
    const url = link.dataset.url
    if (!card || !url) return

    const result = await this.patchStatus(url)
    if (result && result.status) {
      this.applyStatus(card, result.status)
      const select = card.querySelector("[data-status-select]")
      if (select) {
        select.value = result.status
        select.dataset.original = result.status
      }
    }
  }

  // ── Shared helpers ──

  async patchStatus(url, status) {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfMeta ? csrfMeta.content : ""
    const body = status === undefined
      ? undefined
      : JSON.stringify({ business_loan_service: { status } })

    try {
      const response = await fetch(url, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: body
      })
      if (!response.ok) return null
      return await response.json()
    } catch {
      return null
    }
  }

  // Updates the Status badge text, colour, and the card's data-status hook so
  // filter/sort stay consistent after an inline change.
  applyStatus(card, status) {
    card.dataset.status = status

    const badge = card.querySelector("[data-status-badge]")
    if (badge) {
      badge.textContent = this.humanize(status)
      badge.className = `ml-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${this.badgeClass(status)}`
    }
  }

  badgeClass(status) {
    switch (status) {
      case "in_progress":
        return "bg-yellow-100 text-yellow-800"
      case "completed":
        return "bg-green-100 text-green-800"
      case "on_hold":
        return "bg-red-100 text-red-800"
      case "cancelled":
        return "bg-gray-100 text-gray-500"
      case "not_started":
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  humanize(status) {
    const text = (status || "").replace(/_/g, " ")
    return text.charAt(0).toUpperCase() + text.slice(1)
  }

  setFeedback(el, text, className) {
    if (!el) return
    el.textContent = text
    el.className = `text-xs ${className}`
  }
}
